
import React, { useState } from 'react';
import { Mail, Bell, CheckSquare, Search, MoreVertical, Paperclip, Send, Circle, Star, Archive, Trash2, Flag } from 'lucide-react';
import { MOCK_THREADS, MOCK_NOTIFICATIONS, MOCK_TASKS } from '../../constants';
import { Thread, Message, Notification, Task } from '../../types';

export const CommunicationHub: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'inbox' | 'notifications' | 'tasks'>('inbox');
    const [selectedThread, setSelectedThread] = useState<Thread | null>(MOCK_THREADS[0] || null);
    const [replyText, setReplyText] = useState('');

    // Mock sending a message
    const handleSend = () => {
        if (!replyText.trim() || !selectedThread) return;
        // In a real app, this would push to backend
        alert(`Sent: ${replyText}`);
        setReplyText('');
    };

    const renderInbox = () => (
        <div className="flex h-[600px] bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {/* Thread List */}
            <div className="w-1/3 border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                        <input type="text" placeholder="Search messages..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-md" />
                    </div>
                </div>
                <div className="flex-grow overflow-y-auto">
                    {MOCK_THREADS.map(thread => (
                        <div 
                            key={thread.id} 
                            onClick={() => setSelectedThread(thread)}
                            className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${selectedThread?.id === thread.id ? 'bg-blue-50' : ''}`}
                        >
                            <div className="flex justify-between mb-1">
                                <span className="font-medium text-slate-900 truncate">{thread.participants[0].name}</span>
                                <span className="text-xs text-slate-500 whitespace-nowrap">2m ago</span>
                            </div>
                            <div className="text-sm font-medium text-slate-800 mb-1 truncate">{thread.subject}</div>
                            <div className="text-xs text-slate-500 truncate">{thread.lastMessage.content}</div>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* Message View */}
            <div className="w-2/3 flex flex-col">
                {selectedThread ? (
                    <>
                        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                            <div>
                                <h3 className="font-bold text-slate-900">{selectedThread.subject}</h3>
                                <p className="text-xs text-slate-500">with {selectedThread.participants.map(p => p.name).join(', ')}</p>
                            </div>
                            <button className="text-slate-400 hover:text-slate-600"><MoreVertical className="w-5 h-5" /></button>
                        </div>
                        <div className="flex-grow p-4 overflow-y-auto space-y-4 bg-slate-50">
                            {/* Mock History */}
                            <div className="flex justify-start">
                                <div className="bg-white border border-slate-200 rounded-lg rounded-tl-none p-3 max-w-[80%]">
                                    <p className="text-sm text-slate-700">Hi, we are interested in your services for our upcoming IND.</p>
                                    <span className="text-[10px] text-slate-400 mt-1 block">10:00 AM</span>
                                </div>
                            </div>
                            {/* Last Message */}
                            <div className="flex justify-end">
                                <div className="bg-blue-600 text-white rounded-lg rounded-tr-none p-3 max-w-[80%]">
                                    <p className="text-sm">{selectedThread.lastMessage.content}</p>
                                    <span className="text-[10px] text-blue-200 mt-1 block">10:05 AM</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-200 bg-white">
                            <div className="flex items-center gap-2">
                                <button className="text-slate-400 hover:text-slate-600"><Paperclip className="w-5 h-5" /></button>
                                <input 
                                    type="text" 
                                    value={replyText}
                                    onChange={e => setReplyText(e.target.value)}
                                    placeholder="Type a reply..." 
                                    className="flex-grow p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    onKeyDown={e => e.key === 'Enter' && handleSend()}
                                />
                                <button onClick={handleSend} className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"><Send className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                        Select a conversation
                    </div>
                )}
            </div>
        </div>
    );

    const renderNotifications = () => (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <h3 className="font-bold text-slate-900">Notifications</h3>
                <button className="text-xs text-blue-600 font-medium hover:underline">Mark all as read</button>
            </div>
            <div className="divide-y divide-slate-100">
                {MOCK_NOTIFICATIONS.map(note => (
                    <div key={note.id} className={`p-4 hover:bg-slate-50 transition-colors ${!note.read ? 'bg-blue-50/50' : ''}`}>
                        <div className="flex gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!note.read ? 'bg-blue-600' : 'bg-transparent'}`}></div>
                            <div className="flex-grow">
                                <div className="flex justify-between">
                                    <h4 className="text-sm font-medium text-slate-900">{note.title}</h4>
                                    <span className="text-xs text-slate-400">{note.date}</span>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{note.message}</p>
                            </div>
                        </div>
                    </div>
                ))}
                {MOCK_NOTIFICATIONS.length === 0 && <div className="p-8 text-center text-slate-500">No notifications</div>}
            </div>
        </div>
    );

    const renderTasks = () => (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
             <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <h3 className="font-bold text-slate-900">My Tasks</h3>
                <button className="flex items-center text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
                    + Add Task
                </button>
            </div>
            <div className="divide-y divide-slate-100">
                {MOCK_TASKS.map(task => (
                    <div key={task.id} className="p-4 hover:bg-slate-50 flex items-center gap-3 group">
                        <button className={`w-5 h-5 rounded border flex items-center justify-center ${task.completed ? 'bg-green-50 border-green-500 text-green-600' : 'border-slate-300 hover:border-blue-500'}`}>
                            {task.completed && <CheckSquare className="w-3 h-3" />}
                        </button>
                        <div className={`flex-grow ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                            <div className="text-sm font-medium">{task.title}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${task.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {task.priority}
                                </span>
                                <span>Due: {task.dueDate}</span>
                            </div>
                        </div>
                        <button className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex gap-6 mb-6">
                <button 
                    onClick={() => setActiveTab('inbox')}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'inbox' ? 'bg-blue-900 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                >
                    <Mail className="w-4 h-4 mr-2" /> Messages
                </button>
                <button 
                    onClick={() => setActiveTab('notifications')}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'notifications' ? 'bg-blue-900 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                >
                    <Bell className="w-4 h-4 mr-2" /> Notifications
                    {MOCK_NOTIFICATIONS.filter(n => !n.read).length > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {MOCK_NOTIFICATIONS.filter(n => !n.read).length}
                        </span>
                    )}
                </button>
                <button 
                    onClick={() => setActiveTab('tasks')}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'tasks' ? 'bg-blue-900 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                >
                    <CheckSquare className="w-4 h-4 mr-2" /> Tasks
                </button>
            </div>

            {activeTab === 'inbox' && renderInbox()}
            {activeTab === 'notifications' && renderNotifications()}
            {activeTab === 'tasks' && renderTasks()}
        </div>
    );
};
